import { gql } from '@apollo/client';

export const COMMENT_FIELDS = gql`
  fragment CommentFields on Comment {
    as_author
    as_ckbk
    content_id
    created_at
    draft
    id
    safe_html
    star_rating
    image_urls
    comment_image_is_blessed
    type
    updated_at
    country_name
    moderation_status
    moderator_feedback
    parent_id
    user {
      author_id
      avatar
      display_name
      id
    }
  }
`;

export const GET_USER_COMMENTS = gql`
  query GetUserRecipeComments($content_id: String!) {
    UserRecipeFeedback(content_id: $content_id) {
      did_cook
      id
      star_rating
    }
    UserComments(content_id: $content_id) {
      ...CommentFields
      raw_text
    }
    RecipeCommentCounts(content_id: $content_id) {
      id
      review
      question
    }
  }
  ${COMMENT_FIELDS}
`;

export const GET_USER_COMMENT_RESPONSES = gql`
  query GetUserRecipeCommentResponses($content_id: String!) {
    UserComments(content_id: $content_id) {
      ...CommentFields
      raw_text
    }
  }
  ${COMMENT_FIELDS}
`;

export const GET_ALL_RECIPE_COMMENTS = gql`
  query GetAllRecipeComments(
    $content_id: String!
    $type: CommentType
    $offset: Int
    $limit: Int
    $include_rejected: Boolean
    $response_limit: Int
  ) {
    RecipeComments(
      content_id: $content_id
      type: $type
      offset: $offset
      limit: $limit
      include_rejected: $include_rejected
    ) {
      ...CommentFields
      responses(offset: 0, limit: $response_limit, include_rejected: $include_rejected) {
        ...CommentFields
      }
    }
  }
  ${COMMENT_FIELDS}
`;

export const GET_COMMENT_OFFSET = gql`
  query GetRecipeCommentOffset($comment_id: Int) {
    RecipeComment(comment_id: $comment_id) {
      id
      location {
        id
        offset
        latest_id
      }
    }
  }
`;

export const GET_COMMENT_FOR_EDIT = gql`
  query GetRecipeCommentForEdit($comment_id: Int, $include_rejected: Boolean) {
    RecipeComment(comment_id: $comment_id, include_rejected: $include_rejected) {
      as_author
      content_id
      id
      raw_text
      type
      image_urls
      comment_image_is_blessed
      user {
        id
      }
    }
  }
`;

export const SAVE_COMMENTS = gql`
  mutation SaveRecipeComment(
    $content_id: String!
    $content_hash: String
    $id: Int
    $star_rating: Float
    $did_cook: Boolean
    $type: CommentType!
    $raw_text: String!
    $draft: Boolean
    $is_new: Boolean
  ) {
    UserRecipeFeedback(
      content_id: $content_id
      content_hash: $content_hash
      star_rating: $star_rating
      did_cook: $did_cook
    ) {
      id
      star_rating
      did_cook
      ratings {
        average
        count
      }
    }
    UserComment(
      content_id: $content_id
      content_hash: $content_hash
      type: $type
      raw_text: $raw_text
      draft: $draft
      is_new: $is_new
      comment_id: $id
    ) {
      ...CommentFields
      raw_text
    }
    RecipeCommentCounts(content_id: $content_id) {
      id
      review
      question
    }
  }
  ${COMMENT_FIELDS}
`;

export const SAVE_RESPONSE = gql`
  mutation SaveCommentResponse(
    $as_ckbk: Boolean
    $content_hash: String
    $content_id: String!
    $draft: Boolean
    $parent_id: Int
    $raw_text: String!
    $is_new: Boolean
  ) {
    UserComment(
      as_ckbk: $as_ckbk
      content_hash: $content_hash
      content_id: $content_id
      draft: $draft
      parent_id: $parent_id
      raw_text: $raw_text
      type: response
      is_new: $is_new
    ) {
      ...CommentFields
      as_ckbk
      raw_text
    }
  }
  ${COMMENT_FIELDS}
`;

export const MODERATE_COMMENT = gql`
  mutation ModerateRecipeComment(
    $comment_id: Int!
    $content_id: String!
    $moderation_status: ModerationStatus!
    $moderator_feedback: String
  ) {
    RecipeComment(
      comment_id: $comment_id
      moderation_status: $moderation_status
      moderator_feedback: $moderator_feedback
    ) {
      id
      safe_html
      moderation_status
      moderator_feedback
    }
    RecipeCommentCounts(content_id: $content_id) {
      id
      review
      question
    }
  }
`;

export const SAVE_COMMENT_UPDATE = gql`
  mutation SaveRecipeCommentUpdate(
    $comment_id: Int!
    $moderation_status: ModerationStatus!
    $raw_text: String
  ) {
    RecipeComment(
      comment_id: $comment_id
      moderation_status: $moderation_status
      raw_text: $raw_text
    ) {
      id
      safe_html
      raw_text
      moderation_status
      moderator_feedback
    }
  }
`;

export const DELETE_COMMENT = gql`
  mutation DeleteUserComment($comment_id: Int!, $type: String, $content_id: String, $user_id: Int) {
    DeleteUserComment(
      comment_id: $comment_id
      type: $type
      content_id: $content_id
      user_id: $user_id
    ) {
      id
      deleted
    }
  }
`;

export const GENERATE_UPLOAD_REVIEW_IMAGE = gql`
  mutation generateUploadUrl($content_id: String!, $fileType: String!, $user_id: Int) {
    generateUploadUrl(content_id: $content_id, fileType: $fileType, user_id: $user_id)
  }
`;

export const REMOVE_REVIEW_IMAGE = gql`
  mutation removeReviewImage($content_id: String!, $user_id: Int) {
    removeReviewImage(content_id: $content_id, user_id: $user_id)
  }
`;

export const BLESS_REVIEW_IMAGE = gql`
  mutation blessReviewImage(
    $content_id: String!
    $comment_id: Int!
    $user_id: Int!
    $force: Boolean
  ) {
    blessReviewImage(
      content_id: $content_id
      comment_id: $comment_id
      user_id: $user_id
      force: $force
    )
  }
`;

export const UNBLESS_REVIEW_IMAGE = gql`
  mutation unBlessReviewImage($content_id: String!, $comment_id: Int!, $user_id: Int!) {
    unBlessReviewImage(content_id: $content_id, comment_id: $comment_id, user_id: $user_id)
  }
`;
